import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    FormControl,
    FormHelperText,
    FormLabel,
    Rating,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
} from '@mui/material';
import {DesktopDateTimePicker} from '@mui/x-date-pickers/DesktopDateTimePicker';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import BaseDialog from '../../../BaseDialog';
import dayjs, {Dayjs} from '../../../../utils/dayjs';
import PublicOffIcon from "@mui/icons-material/PublicOff";
import PublicIcon from '@mui/icons-material/Public';
import { Story, StoryImage } from '../../../../services/restserver-openapi';
import { useAppDispatch, useAppSelector } from "../../../../hooks/hooks";
import { MultiImageUploader } from "../../../presentationalComponents/MultiImageUploader";
import { asyncUploadImageForStory, removeUpload, resetUploads } from "../../../../store/Vanlifer/Community/storySlice";

type SharingMode = Story.sharingMode;

type EditStoryDialogProps = {
    open: boolean;
    onClose: () => void;
    onSave: (data: {
        title: string;
        description: string;
        rating: number;
        storyDate: number;
        sharingMode: SharingMode;
        storyImages: StoryImage[];
    }) => Promise<void>;
    dialogTitle: string;
    initialTitle?: string;
    initialDescription?: string;
    storyId: number;
    initialImages: StoryImage[];
    initialRating?: number;
    initialStoryDate: number;
    initialSharingMode?: SharingMode;
    stayFrom: number;
    stayTo: number;
};

export default function EditStoryDialog({
                                            open,
                                            onClose,
                                            onSave,
                                            dialogTitle,
                                            initialTitle = '',
                                            initialDescription = '',
                                            storyId,
                                            initialImages,
                                            initialRating = 0,
                                            initialStoryDate,
                                            initialSharingMode = Story.sharingMode.PRIVATE,
                                            stayFrom,
                                            stayTo,
                                        }: EditStoryDialogProps) {
    const dispatch = useAppDispatch();

    const [title, setTitle] = useState(initialTitle);
    const [description, setDescription] = useState(initialDescription);
    const [rating, setRating] = useState<number | null>(initialRating);
    const [storyImages, setStoryImages] = useState<StoryImage[]>(initialImages);
    const [storyDate, setStoryDate] = useState<Dayjs | null>(dayjs(initialStoryDate));
    const [sharingMode, setSharingMode] = useState<SharingMode>(initialSharingMode);

    const [showTitleRequiredHint, setShowTitleRequiredHint] = useState(false);
    const [showRatingRequiredHint, setShowRatingRequiredHint] = useState(false);

    const [isStoryDateValid, setIsStoryDateValid] = useState(true);
    const [saving, setSaving] = useState(false);

    const userEntitlements = useAppSelector(state => state.user.entitlements);

    const canPublishContent = useMemo(() => {
        return userEntitlements.some(entitlement => entitlement.featureId === 'vanliferpublishcontent' && entitlement.value === "true")
    }, [userEntitlements]);

    const uploads = useAppSelector(state => state.story.uploads);

    // Determine if any image is still uploading or has failed; if so, disable the saving
    const isSaveDisabled = useMemo(() => {
        return Object.values(uploads).some(upload =>
            upload.status !== 'success'
        );
    }, [uploads]);

    useEffect(() => {
        if (open) {
            setTitle(initialTitle);
            setDescription(initialDescription);
            setRating(initialRating);
            setStoryImages(initialImages);
            const initialDate = dayjs(initialStoryDate);
            setStoryDate(initialDate);
            setIsStoryDateValid(initialDate.isBetween(dayjs(stayFrom), dayjs(stayTo), null, '[]'));
            setSharingMode(initialSharingMode);

            setShowTitleRequiredHint(false);
            setShowRatingRequiredHint(false);

            dispatch(resetUploads());
        }
    }, [open, initialTitle, initialDescription, initialRating, initialStoryDate, initialSharingMode, stayFrom, stayTo]);


    const handleDateChange = (newValue: Dayjs | null) => {
        setStoryDate(newValue);

        if (!newValue || !newValue.isValid()) {
            setIsStoryDateValid(false);
            return;
        }

        const isValidRange = newValue.isBetween(dayjs(stayFrom), dayjs(stayTo), null, '[]');
        setIsStoryDateValid(isValidRange);
    };

    const handleSave = async () => {
        const trimmedTitle = title.trim();
        const trimmedDescription = description.trim();

        const isTitleValid = trimmedTitle.length > 0 && trimmedTitle.length <= 80;
        const isDescriptionValid = trimmedDescription.length <= 5000;
        const isRatingValid = rating !== null && rating >= 0.5;
        const isDateValid = !!storyDate && storyDate.isValid() && isStoryDateValid;

        setShowTitleRequiredHint(trimmedTitle.length === 0);
        setShowRatingRequiredHint(!isRatingValid);

        if (!isTitleValid || !isDescriptionValid || !isRatingValid || !isDateValid) {
            return;
        }

        setSaving(true);

        try {
            await onSave({
                title: title.trim(),
                description,
                rating: rating ?? 0,
                sharingMode,
                storyDate: storyDate.valueOf(),
                storyImages: storyImages,
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <BaseDialog
            open={open}
            onClose={onClose}
            onSave={handleSave}
            disableSave={isSaveDisabled || saving}
            title={dialogTitle}
            saving={saving}
            maxWidth="md"
        >
            <Box width="100%">
                <Box
                    display="flex"
                    flexWrap="wrap"
                    columnGap={2}
                    rowGap={1}
                    alignItems="flex-start"
                    width="100%"
                >
                    <FormControl
                        required
                        sx={{
                            flexGrow: 1,
                            minWidth: '250px',
                            flexBasis: 'calc(100% - 220px)', // Leave space for rating
                        }}
                    >
                        <TextField
                            fullWidth
                            label="Titel"
                            value={title}
                            onChange={(e) => {
                                const value = e.target.value;
                                setTitle(value);
                                setShowTitleRequiredHint(value.trim() === '');
                            }}
                            required
                            inputProps={{ maxLength: 100 }}
                            error={showTitleRequiredHint || title.trim().length > 80}
                        />
                        <FormHelperText
                            error={showTitleRequiredHint || title.trim().length > 80}
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                            }}
                        >
                            <span style={{ visibility: showTitleRequiredHint ? 'visible' : 'hidden' }}>
                                Titel ist erforderlich.
                            </span>
                                            <span style={{ color: title.trim().length > 80 ? 'red' : undefined }}>
                                {title.trim().length}/80 Zeichen
                            </span>
                        </FormHelperText>
                    </FormControl>

                    <FormControl
                        required
                        sx={{
                            width: '200px',
                            flexGrow: 0,
                            flexShrink: 0,
                            mt: '-4px',
                        }}
                    >
                        <FormLabel component="legend" required
                            sx={{
                                fontSize: '0.75rem',
                                color: 'rgba(0, 0, 0, 0.6)', // matches unfocused MUI label color
                                mb: 0.5,
                            }}
                        >
                            Bewertung
                        </FormLabel>
                        <Rating
                            value={rating}
                            precision={0.5}
                            onChange={(_, newValue) => {
                                setRating(newValue);
                                setShowRatingRequiredHint(!newValue || newValue < 0.5);
                            }}
                            sx={{ fontSize: 28 }}
                        />
                        <FormHelperText
                            error={showRatingRequiredHint}
                            sx={{
                                visibility: showRatingRequiredHint ? 'visible' : 'hidden',
                                alignSelf: 'flex-start',
                            }}
                        >
                            Mind. 0.5 Sterne
                        </FormHelperText>
                    </FormControl>
                </Box>

                <FormControl required fullWidth>
                    <TextField
                        fullWidth
                        label="Beschreibung"
                        multiline
                        minRows={3}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        margin="dense"
                        inputProps={{ maxLength: 5020 }}
                        helperText={
                            <span style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <span style={{ color: description.trim().length > 5000 ? 'red' : undefined }}>
                                    {description.trim().length}/5000 Zeichen
                                </span>
                            </span>
                        }
                        error={description.trim().length > 5000}
                    />
                </FormControl>
            </Box>

            <Box width="100%">
                <FormControl required fullWidth margin="dense">
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DesktopDateTimePicker
                            label="Datum (Zeit nur für Sortierung)"
                            value={storyDate}
                            onChange={handleDateChange}
                            format="DD.MM.YYYY HH:mm"
                            ampm={false}
                            minDateTime={dayjs(stayFrom)}
                            maxDateTime={dayjs(stayTo)}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    margin: 'dense',
                                    required: true,
                                    error: !isStoryDateValid,
                                    helperText: !isStoryDateValid
                                        ? `Das Datum muss zwischen ${dayjs(stayFrom).format('DD.MM.YYYY')} und ${dayjs(stayTo).format('DD.MM.YYYY')} liegen.`
                                        : undefined,
                                },
                            }}
                        />
                    </LocalizationProvider>
                </FormControl>
            </Box>

            <MultiImageUploader
                maxImages={10}
                confirmedImages={storyImages}
                setConfirmedImages={setStoryImages}
                uploads={uploads}
                onUpload={(file, localId) =>
                    dispatch(asyncUploadImageForStory({ storyId, file, localId }))
                }
                getImageId={(img) => img.storyImageId!.toString()}
                getImageUrl={(img) => img.sasDownloadLink!}
                onRemoveUpload={(localId) => dispatch(removeUpload(localId))}
            />

            <Box>
                <FormControl fullWidth margin="normal">
                    <FormLabel
                    >Sichtbarkeit</FormLabel>
                    <ToggleButtonGroup
                        value={sharingMode}
                        exclusive
                        onChange={(_, value) => {
                            if (value !== null) setSharingMode(value);
                        }}
                        fullWidth
                        color="primary"
                    >
                        <Tooltip title="Nur du und Personen mit dem Link können diese Story sehen.">
                            <ToggleButton value={Story.sharingMode.PRIVATE}>
                                <PublicOffIcon sx={{ mr: 1 }} />
                                Privat
                            </ToggleButton>
                        </Tooltip>

                        <Tooltip title="Alle können diese Story sehen, sie wird zudem bei der Unterkunft angezeigt.">
                            <ToggleButton
                                value={Story.sharingMode.PUBLIC}
                                disabled={!canPublishContent}
                            >
                                <PublicIcon sx={{ mr: 1 }} />
                                Öffentlich
                            </ToggleButton>
                        </Tooltip>
                    </ToggleButtonGroup>
                </FormControl>
            </Box>
        </BaseDialog>
    );
}
